import moment from 'moment'

import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { nonNullable } from 'lib/array/arrayUtils'

export interface CustomOfferTravelWindow {
  /** Earliest start across every item in the offer. `YYYY-MM-DD`. */
  start: string
  /** Latest end across every item in the offer. `YYYY-MM-DD`. */
  end: string
}

/**
 * Agent-entered dates are not guaranteed to be plain days, so normalise before
 * comparing. Returns undefined for anything moment cannot parse, so one typo'd
 * item cannot poison the whole offer's window.
 */
function toPlainDay(value: string | undefined): string | undefined {
  if (!value) return undefined

  const parsed = moment.utc(value)
  return parsed.isValid() ? parsed.format(ISO_DATE_FORMAT) : undefined
}

/**
 * The effective travel window of a custom offer, across *all* of its items.
 *
 * A single-dated item -- a one-way flight, a one-night stay -- carries only a
 * start_date, and the write path omits end_date entirely rather than sending
 * null. Such an item is treated as starting and ending on the same day, the
 * same convention getOrderTravelDuration already uses for experience items.
 *
 * Returns null only when no item carries any usable date. That is a genuinely
 * open-dated quote, and callers must keep treating it as active.
 */
export function getCustomOfferTravelWindow(
  item: App.OrderCustomOfferItem,
): CustomOfferTravelWindow | null {
  const offerItems = item.custom_offer?.items ?? []

  const travelWindows = nonNullable(offerItems.map((offerItem) => {
    const start = toPlainDay(offerItem.start_date) ?? toPlainDay(offerItem.end_date)
    const end = toPlainDay(offerItem.end_date) ?? toPlainDay(offerItem.start_date)

    return start && end ? { start, end } : undefined
  }))

  const [firstWindow, ...restWindows] = travelWindows

  if (!firstWindow) return null

  // Every value is a normalised YYYY-MM-DD, so string order is date order.
  return {
    start: restWindows.reduce((earliest, current) => (current.start < earliest ? current.start : earliest), firstWindow.start),
    end: restWindows.reduce((latest, current) => (current.end > latest ? current.end : latest), firstWindow.end),
  }
}
